// src/components/Itinerary/ItineraryHeader.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  IconButton,
  Spacer,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  useToast,
  useColorMode,
  useDisclosure,
  useClipboard,
  Badge,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Tooltip,
  Heading,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";

import { FaShareAlt, FaUserPlus } from "react-icons/fa";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download_2_fill_icon.svg";

import axiosInstance from "../../helpers/axiosInstance";
import InviteManagerModal from "../common/InviteManagerModal";
import Translate from "../../components/LanguageTranslation/Translate";
import TravelGroupCircles from "../common/TravelGroupCircles";
import SettingsMenu from "../common/SettingsMenu";
import { sendInvite, sentInvites } from "../../api/invites";

const ItineraryHeader = ({
  tripId,
  downloadPDF,
  allowedUsers,
  setAllowedUsers,
  isLoading,
}) => {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const history = useHistory();
  const { isOpen, onOpen: openModal, onClose } = useDisclosure();
  const [shareUrl, setShareUrl] = useState("");
  const { hasCopied, onCopy } = useClipboard(shareUrl);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    const fetchCompleted = async () => {
      try {
        const response = await axiosInstance.get(`/bucket-list/${tripId}`);
        if (response.data?.success && response.data?.data) {
          setIsCompleted(Boolean(response.data.data.is_completed));
        }
      } catch (e) {}
    };
    if (tripId) fetchCompleted();
  }, [tripId]);

  /* invite modal */
  const inviteModal = useDisclosure();

  const handleShare = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL; // Use the base URL from .env file
    const generatedUrl = `${baseUrl}/ai-travel-planner/manage-itinerary/${tripId}`;
    setShareUrl(generatedUrl); // Set the share URL in state
    openModal(); // Open the modal
  };

  const handleToggleOngoingTrip = async () => {
    try {
      const response = await axiosInstance.put(
        `/bucket-list/${tripId}/update/status`,
        { is_ongoing: true }
      );

      if (response.data.success) {
        history.replace("/ai-travel-planner/ongoing-trip");
      }
    } catch (error) {
      console.error("Error updating trip status:", error);
    }
  };

  const handleToggleCompletedTrip = async () => {
    const nextCompleted = !isCompleted;
    try {
      const response = await axiosInstance.put(
        `/bucket-list/${tripId}/update/completed`,
        { is_completed: nextCompleted }
      );
      if (response.data.success) {
        setIsCompleted(nextCompleted);
        toast({
          title: nextCompleted ? "Marked as completed" : "Marked as not completed",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        // If marking completed, also remove as ongoing
        if (nextCompleted) {
          try {
            await axiosInstance.put(
              `/bucket-list/${tripId}/update/status`,
              { is_ongoing: false }
            );
            // Optionally navigate away from ongoing list if applicable
            // history.replace("/ai-travel-planner/ongoing-trip");
          } catch (e) {}
        }
      }
    } catch (error) {
      toast({ title: "Unable to update completed status", status: "error" });
    }
  };

  return (
    <Box
      flex="1"
      display="flex"
      flexDirection="column"
      maxH="8vh"
      borderBottom="1px"
      borderColor="blackAlpha.200"
      overflowY="hidden"
    >
      <Box
        display="flex"
        alignItems="center"
        bg={colorMode === "dark" ? "gray.700" : "white"}
        width="100%"
        height="100%"
        p={4}
        pt={0}
        pl={0}
        pb={0}
        boxSizing="border-box"
        overflowY="hidden"
      >
        <IconButton
          icon={<ArrowBackIcon />}
          variant="ghost"
          _hover="none"
          onClick={() => history.goBack()}
        />
        <Text
          fontSize="md"
          fontWeight="bold"
          mb={0}
          color={colorMode === "dark" ? "gray.200" : "blackAlpha.700"}
        >
          <Translate text="Manage Itinerary" />
        </Text>
        <Spacer />
       <TravelGroupCircles tripId={tripId} availableResource="itinerary" />
        <Tooltip label="Mark as Current Trip">
          <Box display="flex" alignItems="center" mr={2}>
            {/* <Text mb={0} mr={2} fontSize={{ base: "xs", md: "sm", lg: "sm" }}>
              <Translate text="Ongoing Trip" />
            </Text> */}
            <Switch
              size="sm"
              isChecked={false}
              onChange={() => handleToggleOngoingTrip()} // Trigger toggle function on change
            />
          </Box>
        </Tooltip>
        <Tooltip label={isCompleted ? "Completed" : "Mark as Completed"}>
          <Box display="flex" alignItems="center" mr={2}>
            <Switch
              size="sm"
              isChecked={isCompleted}
              onChange={handleToggleCompletedTrip}
            />
          </Box>
        </Tooltip>
        <Tooltip label="Download">
          <IconButton
            size="sm"
            icon={<Box as={DownloadIcon} boxSize="5" />}
            aria-label="Download"
            bg="none"
            _hover={{ bg: "none" }}
            onClick={() => {
              toast({
                title: "Download Started",
                description: "Your PDF is being prepared for download. This may take some time...",
                status: "info",
                duration: 4000,
                isClosable: true,
              });
              downloadPDF();
            }}
            isLoading={isLoading}
          />
        </Tooltip>

        <Tooltip label="Invite User">
          <IconButton
            size="sm"
            icon={<Box as={FaUserPlus} boxSize="5" />}
            aria-label="Add"
            bg="none"
            _hover={{ bg: "none" }}
            onClick={inviteModal.onOpen}
          />
        </Tooltip>

        <Tooltip label="Share">
          <IconButton
            size="sm"
            icon={<FaShareAlt />}
            onClick={handleShare} // Call the new share function to open modal
            aria-label="Share"
            bg="none"
            _hover="none"
          />
        </Tooltip>
        <SettingsMenu />
      </Box>
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "xs", md: "md" }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Translate text="Share Profile" />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              <Translate text="Here is the shareable link for the profile:" />
            </Text>
            <Box
              bg={colorMode === "light" ? "gray.100" : "gray.700"}
              p={3}
              borderRadius="md"
              wordBreak="break-all"
            >
              {shareUrl}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onCopy}
              bg="black"
              color="white"
              _hover="none"
              _active="none"
              mr={3}
            >
              <Translate text={hasCopied ? "Copied" : "Copy Link"} />
            </Button>
            <Button variant="ghost" onClick={onClose}>
              <Translate text="Close" />
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ================= Invite / Manage modal ================= */}
      <InviteManagerModal
        tripId={tripId}
        isOpen={inviteModal.isOpen}
        onClose={inviteModal.onClose}
        availableResource="itinerary"
      />
    </Box>
  );
};

export default ItineraryHeader;
